import { Button, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import {
  countPhotoBlurRegions,
  isPhotoPrivacyFinalFailure,
  PHOTO_PRIVACY_CATEGORY_LABELS,
  photoPrivacyCategory,
  type PhotoBlurRegion,
  type PostPhotoPrivacy,
} from '@citizen-walk/shared-types';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import {
  ADMIN_DECISION_LABELS,
  addManualRegion,
  hasUnblurChange,
  rectFromDrag,
  regionKindLabel,
  regionSvgPoints,
  toggleOrRemoveRegion,
} from './photoPrivacyRegions';
import { usePhotoPrivacyAdmin, type PhotoPrivacyAdminAction } from './usePhotoPrivacyAdmin';
import { PanoramaRegionEditor } from './PanoramaRegionEditor';

/**
 * 投稿詳細「写真のぼかし」(docs/28 §10-1・§4-9)。
 *
 * - 状態 (未処理 / 処理中 / 失敗 / 検出あり / 検出なし / 公開しない / 対象外) と件数、失敗の理由
 * - ぼかし済み (公開中) とぼかし前の切り替え (ぼかし前を見られる管理者のみ)
 * - 範囲の編集: 自動の範囲を外す / 戻す、写真の上をドラッグして手でぼかしを追加 (平面写真)
 * - 検出のやり直し、もう一度処理する、最終的な失敗への対応 (手でぼかしを入れて公開 / ぼかしなしで
 *   公開 / 公開しない)、導入前の投稿へのぼかし
 *
 * ぼかし前を見られない地区管理者には「ぼかし前」の表示・範囲を外す操作・ぼかしなしで公開を出さない。
 */
export interface PhotoPrivacySectionProps {
  districtId: string;
  postId: string;
  photoPath: string | null;
  privacy: PostPhotoPrivacy | null;
  isPanorama: boolean;
  /** 公開中の写真の URL (詳細 modal が取得済み)。 */
  publishedUrl: string | null;
  canModerate: boolean;
  /** この管理者がぼかし前の写真を見られるか (master、または地区設定で許可された地区管理者)。 */
  canSeeOriginal: boolean;
}

type Confirm = { action: PhotoPrivacyAdminAction; title: string; message: string } | null;

export function PhotoPrivacySection({
  districtId,
  postId,
  photoPath,
  privacy,
  isPanorama,
  publishedUrl,
  canModerate,
  canSeeOriginal,
}: PhotoPrivacySectionProps) {
  const { toast } = useToast();
  const { busy, run } = usePhotoPrivacyAdmin(districtId, postId);
  const [originalUrl, setOriginalUrl] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PhotoBlurRegion[]>(privacy?.regions ?? []);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const [dragRect, setDragRect] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null,
  );

  // 投稿が更新されたら (処理の完了など) 編集中でなければ範囲を取り直す。
  useEffect(() => {
    if (!editing) setDraft(privacy?.regions ?? []);
  }, [privacy, editing]);

  if (!photoPath && !privacy?.incomingPath) return null;

  const category = photoPrivacyCategory({ photoPath, photoPrivacy: privacy });
  const counts = countPhotoBlurRegions(privacy?.regions);
  const finalFailure = isPhotoPrivacyFinalFailure(privacy);
  const hasOriginal = Boolean(privacy?.originalPath || privacy?.incomingPath);

  async function act(action: PhotoPrivacyAdminAction, regions?: PhotoBlurRegion[]) {
    try {
      const r = await run(action, regions);
      if (action === 'viewOriginal') {
        setOriginalUrl(r.url ?? null);
        setShowOriginal(true);
        return;
      }
      toast(r.outcome === 'failed' ? 'error' : 'success', r.message ?? '処理しました');
      if (action === 'applyRegions') setEditing(false);
    } catch (e) {
      toast('error', (e as Error).message || '操作に失敗しました');
    }
  }

  async function startEdit() {
    setDraft(privacy?.regions ?? []);
    setEditing(true);
    if (canSeeOriginal && hasOriginal && !originalUrl) await act('viewOriginal');
    else if (canSeeOriginal && hasOriginal) setShowOriginal(true);
  }

  function pointFrom(e: PointerEvent<SVGSVGElement>) {
    const b = e.currentTarget.getBoundingClientRect();
    return { x: (e.clientX - b.left) / b.width, y: (e.clientY - b.top) / b.height };
  }

  const viewUrl = showOriginal && originalUrl ? originalUrl : publishedUrl;
  const regionsToShow = editing ? draft : (privacy?.regions ?? []);
  const unblurPending = editing && hasUnblurChange(privacy?.regions ?? [], draft);

  return (
    <section
      className="grid grid-cols-1 gap-2 rounded border border-slate-200 p-3"
      aria-labelledby="photo-privacy-section"
      data-testid="photo-privacy-section"
    >
      <h3 id="photo-privacy-section" className="text-sm font-bold text-slate-900">
        写真のぼかし
      </h3>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className={`rounded px-2 py-0.5 font-bold ${
            category === 'needs-action'
              ? 'bg-danger-100 text-danger-700'
              : category === 'detected' || category === 'not-detected'
                ? 'bg-success-100 text-success-700'
                : 'bg-slate-100 text-slate-700'
          }`}
          data-testid="photo-privacy-category"
        >
          {PHOTO_PRIVACY_CATEGORY_LABELS[category]}
        </span>
        {(counts.face > 0 || counts.licensePlate > 0 || counts.manual > 0) && (
          <span className="text-slate-600">
            顔 {counts.face}・ナンバー {counts.licensePlate}
            {counts.manual > 0 ? `・手で追加 ${counts.manual}` : ''}
          </span>
        )}
        {privacy?.adminDecision && (
          <span className="text-slate-500">
            （管理者が{ADMIN_DECISION_LABELS[privacy.adminDecision.action]}）
          </span>
        )}
      </div>
      {privacy?.status === 'failed' && privacy.errorMessage && (
        <p className="text-xs text-danger-700">
          {privacy.errorMessage}（試行 {privacy.attempts} 回
          {finalFailure ? '・自動の再実行は終了' : '・自動で再実行します'}）
        </p>
      )}
      {!hasOriginal && category !== 'unprocessed' && (
        <p className="text-[11px] text-slate-500">
          ぼかし前の写真は保存されていません（ぼかしを外す・検出のやり直しはできません。手での追加はできます）。
        </p>
      )}

      {/* 写真と範囲 (平面写真は範囲を重ねて表示・編集。360度写真は見回し表示 + 範囲の一覧) */}
      {viewUrl && !isPanorama && (
        <div className="relative mx-auto w-fit max-w-full">
          <img
            src={viewUrl}
            alt={showOriginal ? 'ぼかし前の写真' : 'ぼかし済みの写真'}
            className="block max-h-96 max-w-full"
          />
          {(showOriginal || editing) && (
            <svg
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              className={`absolute inset-0 h-full w-full ${editing ? 'cursor-crosshair touch-none' : ''}`}
              onPointerDown={(e) => {
                if (!editing) return;
                dragStart.current = pointFrom(e);
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerMove={(e) => {
                if (!editing || !dragStart.current) return;
                setDragRect(rectFromDrag(dragStart.current, pointFrom(e), 0));
              }}
              onPointerUp={(e) => {
                if (!editing || !dragStart.current) return;
                const r = rectFromDrag(dragStart.current, pointFrom(e));
                dragStart.current = null;
                setDragRect(null);
                if (r) setDraft((d) => addManualRegion(d, r));
              }}
              data-testid="photo-privacy-overlay"
            >
              {regionsToShow.map((r, i) => (
                <polygon
                  key={i}
                  points={regionSvgPoints(r)}
                  vectorEffect="non-scaling-stroke"
                  className={
                    !r.enabled
                      ? 'fill-transparent stroke-slate-400 [stroke-dasharray:4_3] [stroke-width:2]'
                      : r.source === 'manual'
                        ? 'fill-primary-600/10 stroke-primary-600 [stroke-width:2]'
                        : 'fill-danger-600/10 stroke-danger-600 [stroke-width:2]'
                  }
                />
              ))}
              {dragRect && (
                <rect
                  x={dragRect.x}
                  y={dragRect.y}
                  width={dragRect.w}
                  height={dragRect.h}
                  vectorEffect="non-scaling-stroke"
                  className="fill-primary-600/10 stroke-primary-600 [stroke-width:2]"
                />
              )}
            </svg>
          )}
        </div>
      )}
      {viewUrl && isPanorama && editing && (
        // 360度写真: 見回して画面上をドラッグし、手でぼかしを追加する。
        <PanoramaRegionEditor
          src={viewUrl}
          regions={draft}
          onAdd={(rs) => setDraft((d) => [...d, ...rs])}
          disabled={busy !== null}
          alt={showOriginal ? 'ぼかし前の写真 (360度)' : 'ぼかし済みの写真 (360度)'}
        />
      )}
      {viewUrl && isPanorama && showOriginal && !editing && (
        // 平面写真と同じく、ぼかし前の写真の上にぼかした範囲を重ねて表示する (見回しに追従)。
        <PanoramaRegionEditor
          src={viewUrl}
          regions={privacy?.regions ?? []}
          alt="ぼかし前の写真 (360度)"
        />
      )}
      {(showOriginal || editing) && (
        <p className="text-[11px] text-slate-500">
          赤 = 自動で見つけた範囲、青 = 手で追加した範囲、点線 = 外した範囲（ぼかさない）。
          {editing && !isPanorama && '写真の上をドラッグすると、手でぼかしを追加できます。'}
          {editing &&
            isPanorama &&
            '見回すと範囲も一緒に動きます。外す・戻すは下の一覧で行えます。'}
        </p>
      )}

      {editing && (
        <ul className="grid grid-cols-1 gap-1 text-xs" data-testid="photo-privacy-region-list">
          {draft.map((r, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-2 rounded bg-slate-50 px-2 py-1"
            >
              <span className={r.enabled ? '' : 'text-slate-400 line-through'}>
                {i + 1}. {regionKindLabel(r)}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setDraft((d) => toggleOrRemoveRegion(d, i))}
                disabled={r.source !== 'manual' && r.enabled && !canSeeOriginal}
              >
                {r.source === 'manual' ? '削除' : r.enabled ? '外す' : '戻す'}
              </Button>
            </li>
          ))}
          {draft.length === 0 && <li className="text-slate-500">範囲はありません。</li>}
        </ul>
      )}

      {canModerate && (
        <div className="flex flex-wrap gap-2">
          {category === 'unprocessed' && (
            <Button size="sm" onClick={() => void act('apply')} disabled={busy !== null}>
              {busy === 'apply' ? '処理中…' : 'ぼかしを掛ける'}
            </Button>
          )}
          {!editing && canSeeOriginal && hasOriginal && category !== 'unprocessed' && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                showOriginal
                  ? setShowOriginal(false)
                  : originalUrl
                    ? setShowOriginal(true)
                    : void act('viewOriginal')
              }
              disabled={busy !== null}
              data-testid="photo-privacy-toggle-original"
            >
              {showOriginal ? 'ぼかし済み（公開中）を表示' : 'ぼかし前を表示'}
            </Button>
          )}
          {!editing && (category === 'detected' || category === 'not-detected' || finalFailure) && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void startEdit()}
              disabled={busy !== null}
            >
              {finalFailure ? '手でぼかしを入れて公開する' : '範囲を編集'}
            </Button>
          )}
          {editing && (
            <>
              <Button
                size="sm"
                onClick={() => void act('applyRegions', draft)}
                disabled={busy !== null || (unblurPending && !canSeeOriginal)}
              >
                {busy === 'applyRegions' ? '保存中…' : 'この範囲でぼかして公開'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setEditing(false)}
                disabled={busy !== null}
              >
                やめる
              </Button>
            </>
          )}
          {!editing && (category === 'detected' || category === 'not-detected') && hasOriginal && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                setConfirm({
                  action: 'redetect',
                  title: '検出をやり直しますか？',
                  message:
                    'ぼかし前の写真から顔・ナンバーを検出し直します。外した範囲は元に戻ります（手で追加した範囲は残ります）。',
                })
              }
              disabled={busy !== null}
            >
              検出をやり直す
            </Button>
          )}
          {!editing && privacy?.status === 'failed' && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void act('retry')}
              disabled={busy !== null}
            >
              {busy === 'retry' ? '処理中…' : 'もう一度処理する'}
            </Button>
          )}
          {!editing && finalFailure && canSeeOriginal && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setConfirm({
                    action: 'publishUnblurred',
                    title: 'ぼかしを掛けずに公開しますか？',
                    message:
                      'ぼかし前の写真をそのまま公開します。顔やナンバーが写っていないことを確認してから選んでください。',
                  })
                }
                disabled={busy !== null}
              >
                ぼかしなしで公開する
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setConfirm({
                    action: 'withhold',
                    title: 'この写真を公開しないことにしますか？',
                    message:
                      '写真はほかの参加者に公開されません（投稿の本文は公開のままです）。投稿者には「管理者の判断で写真は公開されません」と表示されます。',
                  })
                }
                disabled={busy !== null}
              >
                公開しない
              </Button>
            </>
          )}
          {!editing && finalFailure && !canSeeOriginal && (
            <span className="text-xs text-slate-600">
              要対応（この地区ではマスター管理者が対応します）
            </span>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirm !== null}
        intent="warning"
        title={confirm?.title ?? ''}
        message={confirm?.message ?? ''}
        confirmLabel="実行する"
        onConfirm={() => {
          const a = confirm?.action;
          setConfirm(null);
          if (a) void act(a);
        }}
        onCancel={() => setConfirm(null)}
      />
    </section>
  );
}
